/** TanStack Query hooks for the admin markets surface (P1 Tier-A). */
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
} from "@tanstack/react-query";
import {
  createMarket,
  deleteMarket,
  listMarkets,
  marketKeys,
  updateMarket,
  type AdminMarket,
  type AdminMarketCreate,
  type AdminMarketListResponse,
  type AdminMarketUpdate,
} from "@/features/admin/markets/api";

const PAGE_LIMIT = 100;

export function useMarkets(): UseInfiniteQueryResult<
  InfiniteData<AdminMarketListResponse>,
  Error
> {
  return useInfiniteQuery<
    AdminMarketListResponse,
    Error,
    InfiniteData<AdminMarketListResponse>,
    ReturnType<typeof marketKeys.list>,
    string | null
  >({
    queryKey: marketKeys.list({}),
    queryFn: ({ pageParam }) => listMarkets({ cursor: pageParam, limit: PAGE_LIMIT }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    staleTime: 10_000,
  });
}

function useInvalidateMarkets(): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["admin", "markets"] });
}

export function useCreateMarket(): UseMutationResult<AdminMarket, Error, AdminMarketCreate> {
  const invalidate = useInvalidateMarkets();
  return useMutation<AdminMarket, Error, AdminMarketCreate>({
    mutationFn: (body) => createMarket(body),
    onSuccess: () => invalidate(),
  });
}
export function useUpdateMarket(): UseMutationResult<
  AdminMarket,
  Error,
  { id: string; body: AdminMarketUpdate }
> {
  const invalidate = useInvalidateMarkets();
  return useMutation<AdminMarket, Error, { id: string; body: AdminMarketUpdate }>({
    mutationFn: ({ id, body }) => updateMarket(id, body),
    onSuccess: () => invalidate(),
  });
}
export function useDeleteMarket(): UseMutationResult<AdminMarket, Error, string> {
  const invalidate = useInvalidateMarkets();
  return useMutation<AdminMarket, Error, string>({
    mutationFn: (id) => deleteMarket(id),
    onSuccess: () => invalidate(),
  });
}
